/**
 * Hidabrut WebView mode. The Hidabrut app opens Kol Torah in a WebView and injects
 * `window.hidabrotPayload` (a Hidabrut user + one of its profiles, like Netflix
 * profiles) instead of a login. Each profile is its own Kol Torah user.
 *
 * Kept free of Firebase imports: App.tsx uses it to decide which bundle to load.
 * The Firebase session lives in hidabrutSession.ts.
 */

export interface HidabrutPayload {
  profileName: string;
  email: string;
  hidabrutProfileId: string;
  hidabrotUserId: string;
  hidabrotToken: string;
}

declare global {
  interface Window {
    hidabrotPayload?: Partial<HidabrutPayload>;
  }
}

// Must match createHidabrutSession (serverless/functions/src/index.ts).
const HIDABRUT_ID_RE = /^[A-Za-z0-9_-]{1,60}$/;
const HIDABRUT_EMAIL_DOMAIN = 'hidabrut.invalid';

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** The injected payload, or null when not running inside Hidabrut (or it's malformed). */
export function getHidabrutPayload(): HidabrutPayload | null {
  if (typeof window === 'undefined') return null;
  const raw = window.hidabrotPayload;
  if (!raw || typeof raw !== 'object') return null;
  const hidabrotUserId = asTrimmedString(raw.hidabrotUserId);
  const hidabrutProfileId = asTrimmedString(raw.hidabrutProfileId);
  if (!HIDABRUT_ID_RE.test(hidabrotUserId) || !HIDABRUT_ID_RE.test(hidabrutProfileId)) {
    return null;
  }
  return {
    hidabrotUserId,
    hidabrutProfileId,
    profileName: asTrimmedString(raw.profileName),
    email: asTrimmedString(raw.email),
    hidabrotToken: asTrimmedString(raw.hidabrotToken),
  };
}

export function isHidabrut(): boolean {
  return getHidabrutPayload() !== null;
}

/** Firebase uid of a Hidabrut profile. */
export function getHidabrutUid(payload: HidabrutPayload): string {
  return `hid_${payload.hidabrotUserId}_${payload.hidabrutProfileId}`;
}

/** Synthetic per-profile email (profiles share the real Hidabrut email). */
export function getHidabrutProfileEmail(payload: HidabrutPayload): string {
  return `${getHidabrutUid(payload).toLowerCase()}@${HIDABRUT_EMAIL_DOMAIN}`;
}

// --- Profile switching -------------------------------------------------------
// Profiles share the WebView's localStorage, and progress/selections live only in
// localStorage. When the active profile changes, the previous profile's data is
// stashed under its uid and the new profile's stash (if any) is restored.

const ACTIVE_UID_KEY = 'hidabrutActiveUid';
const STASH_PREFIX = 'hidabrutProfileStash:';
// Device-level keys that are shared by all profiles and never stashed.
const DEVICE_KEYS = new Set([ACTIVE_UID_KEY, 'i18nextLng', 'react_id', 'app', 'showPayments']);

function isDeviceKey(key: string): boolean {
  return DEVICE_KEYS.has(key) || key.startsWith(STASH_PREFIX) || key.startsWith('mp_');
}

function profileKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key !== null && !isDeviceKey(key)) keys.push(key);
  }
  return keys;
}

/**
 * Call once at startup, before anything reads localStorage. Swaps the profile-specific
 * localStorage when Hidabrut opened us with a different profile than last time.
 * Returns true when the active profile changed.
 */
export function prepareHidabrutProfileStorage(): boolean {
  const payload = getHidabrutPayload();
  if (!payload) return false;
  try {
    const uid = getHidabrutUid(payload);
    const activeUid = localStorage.getItem(ACTIVE_UID_KEY);
    if (activeUid === uid) return false;

    const keys = profileKeys();
    if (activeUid) {
      const stash: Record<string, string> = {};
      keys.forEach((key) => {
        const value = localStorage.getItem(key);
        if (value !== null) stash[key] = value;
      });
      try {
        localStorage.setItem(STASH_PREFIX + activeUid, JSON.stringify(stash));
      } catch (e) {
        console.error('[Hidabrut] Failed to stash previous profile data:', e);
      }
    }
    keys.forEach((key) => localStorage.removeItem(key));

    const saved = localStorage.getItem(STASH_PREFIX + uid);
    if (saved) {
      const restored = JSON.parse(saved) as Record<string, string>;
      Object.entries(restored).forEach(([key, value]) => localStorage.setItem(key, value));
      localStorage.removeItem(STASH_PREFIX + uid);
    }
    localStorage.setItem(ACTIVE_UID_KEY, uid);
    return true;
  } catch (e) {
    console.error('[Hidabrut] Profile storage switch failed:', e);
    return false;
  }
}
